// import React from 'react';
import { styled } from '@mui/material/styles';
import { sortBy } from 'lodash';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { SdgScoreClobDto, ReportAppendix, CompanyQuestionAnswerPairDto } from 'models/dtoTypes';
import SubSection from './_subSection';
import DoNoSignificantHarmClob from './_doNoSignificantHarmClob';
import ReportAppendixClob from './_reportAppendixClob';
import Empty from './_empty';
import { sdgSvg } from 'kosmos/images';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { GetFilePublicUrl } from 'services/openapiClient';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

const PREFIX = 'DoNoSignificantHarmReport';

const classes = {
  item: `${PREFIX}-item`,
  content: `${PREFIX}-content`,
  btnContainer: `${PREFIX}-btnContainer`,
  stackContainer: `${PREFIX}-stackContainer`,
  listContainer: `${PREFIX}-listContainer`,
};

const Root = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  [`& .${classes.item}`]: {
    width: '100%',
    marginBottom: 20,
  },
  [`& .${classes.content}`]: {
    position: 'relative',
  },
  [`& .${classes.btnContainer}`]: {
    position: 'absolute',
    right: 0,
    top: 10,
    zIndex: 100,
  },
  [`& .${classes.stackContainer}`]: {
    paddingLeft: 32,
  },
  [`& .${classes.listContainer}`]: {
    marginBottom: 20,
  },
}));

const CustomStack = styled(Stack)({
  flexWrap: 'wrap',
  gap: '8px',
});

const CustomChip = styled(Chip)({
  margin: '0px !important',
});

interface IProps {
  sdgReport: SdgScoreClobDto[];
  sdgReportSmartGovernance?: SdgScoreClobDto[];
  ghgScopeSupplyChainAppendixReport: ReportAppendix[];
  ghgScopeSupplyChainAppendixReportSmartGovernance?: ReportAppendix[];
  isSmartGovVerified: boolean;
  companyQuestionnaireDetail?: CompanyQuestionAnswerPairDto[];
}

interface CompanyQuestionAnswerPairFinalDto {
  question?: string | null;
  questionAlias?: string | null;
  section?: string | null;
  isSatisfied?: boolean;
  answer?: string | null;
  filesList?: Array<FileDto> | null;
  answerList?: Array<string> | null;
  optionsList?: Array<OptionDto> | null;
  answerDate?: string;
  questionType?: string | null;
}

interface OptionDto {
  key?: string | null;
  values?: Array<string> | null;
}

interface FileDto {
  fileName?: string | null;
  fileUrl?: string | null;
}

export default function Appendix(props: IProps): JSX.Element {
  const { t } = useTranslation('default');
  const {
    sdgReport,
    sdgReportSmartGovernance,
    ghgScopeSupplyChainAppendixReport,
    ghgScopeSupplyChainAppendixReportSmartGovernance,
    isSmartGovVerified,
    companyQuestionnaireDetail,
  } = props;

  const renderUnsdgSimpleItem = (item: SdgScoreClobDto, index: number) => {
    const { sdgNumber, title, affinity, associatedNaceCodes } = item;
    const key = title ? title.toString().replace(/\W/g, '-').toLowerCase() : '';
    const itemSg = sdgReportSmartGovernance && sdgReportSmartGovernance[index];
    return (
      <div key={index} className={classes.item}>
        <DoNoSignificantHarmClob
          sdgId={`${key}-appendix`}
          title={`${sdgNumber || 0}. ${title || ''}`}
          affinity={affinity}
          associatedNaceCodes={associatedNaceCodes || []}
          autoHeight={true}
        />
        {itemSg && (
          <DoNoSignificantHarmClob
            title={`${itemSg.sdgNumber || 0}. ${itemSg.title || ''}`}
            secondaryAffinity={itemSg.affinity}
            associatedNaceCodes={itemSg.associatedNaceCodes || []}
            autoHeight={true}
          />
        )}
      </div>
    );
  };

  const renderGhgSimpleItem = (item: ReportAppendix, index: number) => {
    const { title, affinity, details } = item;
    const detailList = Object.values(details || {});
    const key: string = title ? title.toString().replace(/\W/g, '-').toLowerCase() : '';
    const itemSg =
      ghgScopeSupplyChainAppendixReportSmartGovernance &&
      ghgScopeSupplyChainAppendixReportSmartGovernance[index];
    return (
      <div key={index} className={classes.item}>
        <ReportAppendixClob
          sdgId={`${key}-appendix`}
          title={title || ''}
          affinity={affinity}
          detailList={detailList || []}
          autoHeight={true}
        />
        {itemSg && (
          <ReportAppendixClob
            title={itemSg.title || ''}
            secondaryAffinity={itemSg.affinity}
            detailList={Object.values(itemSg.details || {}) || []}
            autoHeight={true}
          />
        )}
      </div>
    );
  };
  const renderUnsdgsContent = () => {
    const sdgHasAppendix = sdgReport.filter(
      (s) => s.associatedNaceCodes && s.associatedNaceCodes.length > 0,
    );
    if (sdgHasAppendix.length === 0) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return <div>{sdgHasAppendix.map(renderUnsdgSimpleItem)}</div>;
  };

  const renderGhgContent = () => {
    const ghgAppendix = sortBy(ghgScopeSupplyChainAppendixReport, (i) => i.sortOrder);
    if (ghgAppendix.length === 0) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return <div>{ghgAppendix.map(renderGhgSimpleItem)}</div>;
  };

  const renderQuestionAnswer = (pair: CompanyQuestionAnswerPairFinalDto) => {
    if (!pair.question) {
      return <></>;
    }
    if (pair.questionType === 'MultipleChoice') {
      if (pair.answerList && pair.answerList.length > 1) {
        return (
          <div key={pair.question}>
            <Typography variant="body1" component="p" sx={{ fontWeight: 'bolder', pt: 2, pb: 1 }}>
              {pair.question}
            </Typography>
            <List>
              {pair.answerList &&
                pair.answerList.map((a: string, i: number) => (
                  <li key={`section-${a}=${i}`}>
                    <ListItem key={`item-${a}-${i}`}>
                      <ListItemText primary={`${i + 1}. ${a}`} />
                    </ListItem>
                  </li>
                ))}
            </List>
            <Divider />
          </div>
        );
      } else {
        return (
          <div key={pair.question}>
            <Typography variant="body1" component="p" sx={{ fontWeight: 'bolder', pt: 2, pb: 1 }}>
              {pair.question}
            </Typography>
            <Typography variant="body1" component="p" sx={{ pt: 1, pb: 2 }}>
              {(pair.answerList && pair.answerList[0]) || 'Question not answered.'}
            </Typography>
            <Divider />
          </div>
        );
      }
    } else if (pair.questionType === 'Tabular') {
      if (pair.optionsList && pair.optionsList.length > 0) {
        return (
          <div key={pair.question}>
            <Typography variant="body1" component="p" sx={{ fontWeight: 'bolder', pt: 2, pb: 1 }}>
              {pair.question}
            </Typography>
            <List className={classes.listContainer}>
              {pair.optionsList &&
                pair.optionsList.map((a: OptionDto, i: number) => (
                  <div key={`optionList-${i}`}>
                    {a.key && a.values ? (
                      <div key={`item-${a.key}-${i}`}>
                        <ListItem key={`item-${a.key}-${i}-list`}>
                          <ListItemText primary={`${i + 1}. ${a.key}`} />
                        </ListItem>
                        <div
                          className={classes.stackContainer}
                          key={`item-${a.key}-${i}-stack-container`}
                        >
                          <CustomStack direction="row" spacing={1} key={`item-${a.key}-${i}-stack`}>
                            {a.values &&
                              a.values.map((b: string) => (
                                <CustomChip label={b} key={`item-${b}-chip`} variant="outlined" />
                              ))}
                          </CustomStack>
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                ))}
            </List>
            <Divider />
          </div>
        );
      } else {
        return (
          <div key={pair.question}>
            <Typography variant="body1" component="p" sx={{ fontWeight: 'bolder', pt: 2, pb: 1 }}>
              {pair.question}
            </Typography>
            <Typography variant="body1" component="p" sx={{ pt: 1, pb: 2 }}>
              Question not answered.
            </Typography>
            <Divider />
          </div>
        );
      }
    } else if (pair.questionType === 'File') {
      return (
        <div key={pair.question}>
          <div className={classes.listContainer}>
            <Typography variant="body1" component="p" sx={{ fontWeight: 'bolder', pt: 2, pb: 1 }}>
              {pair.question}
            </Typography>
            <CustomStack direction="row" spacing={1} key={`item-${pair.question}-stack`}>
              {pair.filesList &&
                pair.filesList.map((a: FileDto, i: number) => (
                  <div key={`optionList-${i}`}>
                    {a.fileUrl ? (
                      <CustomChip
                        icon={<DownloadIcon />}
                        label={a.fileName}
                        onClick={() => {
                          getFilePublicUrl(a.fileUrl || '');
                        }}
                        clickable
                        variant="outlined"
                      />
                    ) : (
                      <CustomChip
                        label={
                          a?.fileName
                            ? `${a.fileName} (File not uploaded properly)`
                            : 'File not uploaded properly'
                        }
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </div>
                ))}
            </CustomStack>
          </div>
          <Divider />
        </div>
      );
    } else {
      return (
        <div key={pair.question}>
          <Typography variant="body1" component="p" sx={{ fontWeight: 'bolder', pt: 2, pb: 1 }}>
            {pair.question}
          </Typography>
          <Typography variant="body1" component="p" sx={{ pt: 1, pb: 2 }}>
            {pair.answer}
          </Typography>
          <Divider />
        </div>
      );
    }
  };

  const getFilePublicUrl = async (filePrivateUrl: string) => {
    const url = await GetFilePublicUrl(filePrivateUrl);
    window.open(url?.data, '_blank');
  };

  const renderSelfReportingQuestions = () => {
    if (!companyQuestionnaireDetail?.length) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    const finalQuestionnaire: Array<CompanyQuestionAnswerPairFinalDto> = generateFinalQuestionnaire(
      companyQuestionnaireDetail,
    );

    return (
      <div>
        {(finalQuestionnaire || []).map((pair: CompanyQuestionAnswerPairFinalDto) =>
          renderQuestionAnswer(pair),
        )}
      </div>
    );
  };

  const generateFinalQuestionnaire = (questions: CompanyQuestionAnswerPairDto[]) => {
    const answers: Array<CompanyQuestionAnswerPairFinalDto> = [];
    const questionObj = {};
    questions.forEach((a) => {
      const alias = a.questionAlias || '';
      if (!questionObj[alias]) {
        questionObj[alias] = [a];
      } else {
        questionObj[alias].push(a);
      }
    });
    for (const key in questionObj) {
      const answer = questionObj[key][0];
      if (answer.questionType === 'File') {
        answer.filesList = [{ fileName: answer.answer, fileUrl: answer.fileUrl }];
        for (let i = 1; i < questionObj[key].length; i += 1) {
          answer.filesList.push({
            fileName: questionObj[key][i].answer,
            fileUrl: questionObj[key][i].fileUrl,
          });
        }
      }
      answers.push(answer);
    }
    return answers;
  };

  return (
    <Root>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', marginBottom: 4 }}>
        {t('appendix')}
      </Typography>
      <div className={classes.content}>
        <SubSection
          title={t('united-nations-sustainable-development-goals-sdgs-scoring-breakdown')}
          icon={<img src={sdgSvg} />}
        >
          {renderUnsdgsContent()}
        </SubSection>
        <div id="ghg-appendix"></div>
        <SubSection title={t('ghg-scope-3-supply-chains')} icon={<img src={sdgSvg} />}>
          {renderGhgContent()}
        </SubSection>
        {isSmartGovVerified && (
          <SubSection title="" icon={<img src={sdgSvg} />}>
            {renderSelfReportingQuestions()}
          </SubSection>
        )}
      </div>
    </Root>
  );
}
